import { StaticPageLayout } from '../layouts/static-page-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MessageCircle, BookOpen, Settings, Users, HelpCircle, Mail, ArrowRight, Search, Phone, Clock, LifeBuoy } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export function HelpPage() {
  const navigate = useNavigate();
  
  // Set page title
  useEffect(() => {
    document.title = 'Help Center - AceHorizon';
  }, []);

  const handleContactSupport = () => {
    navigate('/contact', { state: { scrollToForm: true } });
  };
  const quickHelpCards = [
    {
      icon: BookOpen,
      title: "Getting Started",
      description: "Learn the basics of using our platform",
      content: "New to Ace Horizon? Start here to learn how to create your account and begin your learning journey.",
      color: "bg-blue-500",
      gradient: "from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20"
    },
    {
      icon: Settings,
      title: "Account Settings",
      description: "Manage your profile and preferences",
      content: "Learn how to customize your learning experience and manage your account settings.",
      color: "bg-green-500",
      gradient: "from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20"
    },
    {
      icon: MessageCircle,
      title: "AI Tutoring",
      description: "Make the most of our AI features",
      content: "Discover how to effectively interact with our AI tutor for personalized learning.",
      color: "bg-purple-500",
      gradient: "from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20"
    }
  ];

  const faqs = [
    {
      question: "How do I get started with Ace Horizon?",
      answer: "Simply create an account, complete your profile setup, and start exploring our learning materials. Our AI will personalize content based on your learning preferences and progress."
    },
    {
      question: "Is Ace Horizon suitable for all grade levels?",
      answer: "Currently, we focus on Class 9 and above, with comprehensive coverage of subjects like Mathematics, Science, and English. We're continuously expanding our content library."
    },
    {
      question: "How does the AI tutoring work?",
      answer: "Our AI analyzes your learning patterns, identifies areas where you need help, and provides personalized explanations, practice problems, and study recommendations."
    },
    {
      question: "Can I track my learning progress?",
      answer: "Yes! Your dashboard shows detailed progress tracking, including completed topics, quiz scores, time spent learning, and areas for improvement."
    },
    {
      question: "Is my data safe and secure?",
      answer: "Absolutely. We use industry-standard encryption and security measures to protect your personal information and learning data. See our Privacy Policy for more details."
    },
    {
      question: "How can I contact support?",
      answer: "You can reach our support team through the contact form, email us at support@acehorizon.ai."
    }
  ];

  return (
    <StaticPageLayout>
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
        
        {/* Hero Section */}
        <div className="text-center py-5 sm:py-12">
          <div className="max-w-4xl mx-auto px-2 sm:px-4">
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-6 bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 dark:from-cyan-400 dark:via-blue-500 dark:to-purple-500 bg-clip-text text-transparent leading-tight px-2">
              Help & Support
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed px-2">
              Find answers to common questions and get help with 
              <span className="text-primary font-semibold"> using Ace Horizon effectively</span>. Our support team is here to ensure your 
              <span className="text-primary font-semibold"> learning success</span>.
            </p>
          </div>
        </div>

        {/* Quick Help Cards */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 dark:from-emerald-400 dark:via-teal-400 dark:to-cyan-400 bg-clip-text text-transparent">Quick Help Topics</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Get started quickly with these essential topics
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {quickHelpCards.map((card, index) => {
              const IconComponent = card.icon;
              return (
                <Card key={index} className="border-2">
                  <div className={`absolute inset-0 bg-gradient-to-r ${card.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none`}></div>
                  <CardHeader className="relative z-10">
                    <div className={`w-12 h-12 ${card.color} rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-xl font-semibold">{card.title}</CardTitle>
                    <CardDescription className="text-muted-foreground">
                      {card.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="relative z-10">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {card.content}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 dark:from-violet-400 dark:via-purple-400 dark:to-fuchsia-400 bg-clip-text text-transparent">Frequently Asked Questions</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Find answers to the most common questions about Ace Horizon
            </p>
          </div>
          
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-primary/20">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none"></div>
                <CardContent className="p-6 relative z-10">
                  <div className="flex items-start space-x-4">
                    <div className="w-2 h-2 bg-primary rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <h3 className="text-lg font-semibold mb-3 group-hover:text-primary transition-colors duration-300">
                        {faq.question}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-2xl p-8 text-center mb-8">
          <div className="max-w-2xl mx-auto">
            <LifeBuoy className="w-16 h-16 mx-auto text-primary mb-6" />
            <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-indigo-600 via-blue-600 to-teal-600 dark:from-indigo-400 dark:via-blue-400 dark:to-teal-400 bg-clip-text text-transparent">Still Need Help?</h2>
            <p className="text-lg mb-8 text-muted-foreground leading-relaxed">
              Can't find what you're looking for? Our dedicated support team is here to help you succeed on your learning journey.
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center">
              <Button size="lg" className="px-8 py-3" onClick={handleContactSupport}>
                <Mail className="mr-2 h-5 w-5" />
                Contact Support
              </Button>
            </div>
            
            <p className="text-sm text-muted-foreground mt-6">
              Average response time: <span className="text-primary font-medium">Less than 24 hours</span>
            </p>
          </div>
        </div>
      </div>
    </StaticPageLayout>
  );
}
